import { Header } from "@/components/Header";
import { PremiumFooter } from "@/components/landing/PremiumFooter";
import { Handshake, Zap, Globe, Award, ArrowRight, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";

const partnerTypes = [
  {
    icon: Zap,
    title: "Partenaires Technologiques",
    description: "Intégrez Floop dans votre écosystème produit et offrez de nouvelles fonctionnalités à vos utilisateurs.",
    benefits: [
      "Accès API prioritaire",
      "Support technique dédié",
      "Co-marketing opportunités",
      "Tarifs préférentiels",
    ],
  },
  {
    icon: Globe,
    title: "Revendeurs & Agences",
    description: "Proposez Floop à vos clients et générez des revenus récurrents grâce à notre programme partenaire.",
    benefits: [
      "Commissions attractives",
      "Formation commerciale",
      "Ressources marketing",
      "Dashboard partenaire",
    ],
  },
  {
    icon: Award,
    title: "Ambassadeurs & Créateurs",
    description: "Partagez votre passion pour Floop et bénéficiez d'avantages exclusifs en tant qu'ambassadeur.",
    benefits: [
      "Crédits gratuits",
      "Accès early access",
      "Visibilité sur nos canaux",
      "Événements exclusifs",
    ],
  },
];

const currentPartners = [
  { name: "Creative Studio Pro", type: "Agence" },
  { name: "DesignTech Solutions", type: "Technologie" },
  { name: "Marketing Factory", type: "Agence" },
  { name: "CloudVision AI", type: "Technologie" },
  { name: "Digital Creators Co", type: "Créateurs" },
  { name: "InnovateLab", type: "Technologie" },
];

const Partners = () => {
  return (
    <div className="min-h-screen bg-background">
      <Header />

      {/* Hero */}
      <section className="pt-24 pb-16 text-center">
        <div className="container mx-auto px-6">
          <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-6">
            <Handshake className="w-8 h-8 text-primary" />
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-6 font-display">
            Devenez partenaire{" "}
            <span className="bg-gradient-floop bg-clip-text text-transparent">
              Floop
            </span>
          </h1>
          <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto mb-8">
            Rejoignez notre écosystème de partenaires et développez votre activité 
            avec la plateforme de génération d'images IA leader du marché.
          </p>
          <Button size="lg" className="bg-gradient-floop hover:opacity-90 text-white rounded-full px-8">
            Devenir partenaire
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </section>

      {/* Partner Types */}
      <section className="py-16 bg-secondary/30">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-foreground mb-4 font-display">
              Programmes partenaires
            </h2>
            <p className="text-muted-foreground">
              Choisissez le programme adapté à votre activité.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {partnerTypes.map((type, index) => (
              <div
                key={index}
                className="bg-card border border-border rounded-2xl p-6 hover:border-primary/50 transition-colors"
              >
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
                  <type.icon className="w-6 h-6 text-primary" />
                </div>
                <h3 className="text-lg font-semibold text-foreground mb-2">{type.title}</h3>
                <p className="text-sm text-muted-foreground mb-4">{type.description}</p>
                <ul className="space-y-2">
                  {type.benefits.map((benefit, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm">
                      <Check className="w-4 h-4 text-success" />
                      <span className="text-muted-foreground">{benefit}</span>
                    </li>
                  ))}
                </ul>
                <Button variant="outline" className="w-full mt-6 rounded-xl">
                  En savoir plus
                </Button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Current Partners */}
      <section className="py-16">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-foreground mb-4 font-display">
              Ils nous font confiance
            </h2>
          </div>
          <div className="flex flex-wrap justify-center gap-4 max-w-4xl mx-auto">
            {currentPartners.map((partner, index) => (
              <div
                key={index}
                className="px-6 py-3 bg-card border border-border rounded-full"
              >
                <span className="font-medium text-foreground">{partner.name}</span>
                <span className="text-muted-foreground text-sm ml-2">• {partner.type}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gradient-floop/10">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold text-foreground mb-4 font-display">
            Prêt à nous rejoindre ?
          </h2>
          <p className="text-muted-foreground mb-8 max-w-xl mx-auto">
            Contactez notre équipe partenariats pour discuter de votre projet.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/contact">
              <Button size="lg" className="bg-gradient-floop hover:opacity-90 text-white rounded-full px-8">
                Contacter l'équipe
              </Button>
            </Link>
            <a href="mailto:partners@floop.ai">
              <Button size="lg" variant="outline" className="rounded-full px-8">
                partners@floop.ai
              </Button>
            </a>
          </div>
        </div>
      </section>

      <PremiumFooter />
    </div>
  );
};

export default Partners;
